import React, { useEffect, useState, useCallback } from "react";
import { useLiveRefresh } from "@/hooks/useLiveRefresh";
import { LedgerTable } from "@/components/v3/LedgerTable";
import { HistoricalBookBasis } from "@/components/v3/HistoricalBookBasis";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { formatINRFull, formatNumber, formatCr } from "@/lib/format";
import { Search, ChevronLeft, ChevronRight, ArrowUpDown, Filter, Download, Star } from "lucide-react";

const BUCKET_COLORS = {
  "On time": "bg-emerald-50 text-emerald-700 border-emerald-200",
  "0-30 DPD": "bg-lime-50 text-lime-700 border-lime-200",
  "31-60 DPD": "bg-amber-50 text-amber-700 border-amber-200",
  "61-90 DPD": "bg-orange-50 text-orange-700 border-orange-200",
  "91-365 DPD": "bg-red-50 text-[#b3122a] border-red-200",
  "Closed": "bg-slate-100 text-slate-500 border-slate-200",
};

function Bucket({ b }) {
  return (
    <span className={`px-2.5 py-1 rounded-full text-xs font-semibold border whitespace-nowrap ${BUCKET_COLORS[b] || "bg-slate-100 text-slate-600 border-slate-200"}`}>
      {b}
    </span>
  );
}

function Select({ value, onChange, options, label, testid }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
      className="px-3 py-2.5 rounded-sm border border-slate-300 bg-white text-sm font-medium text-slate-700 focus:border-[#b3122a] focus:ring-2 focus:ring-[#b3122a]/20 outline-none transition cursor-pointer"
    >
      <option value="all">{label}</option>
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  );
}

export default function Portfolio() {
  const [version] = useLiveRefresh();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [data, setData] = useState({ items: [], total: 0, page: 1 });
  const [filters, setFilters] = useState({ products: [], geographies: [], buckets: [] });
  const [search, setSearch] = useState("");
  const [product, setProduct] = useState(searchParams.get("product") || "all");
  const [geography, setGeography] = useState(searchParams.get("geography") || "all");
  const [bucket, setBucket] = useState(searchParams.get("bucket") || "all");
  const [industry, setIndustry] = useState(searchParams.get("industry") || "all");
  const [borrower, setBorrower] = useState(searchParams.get("borrower") || "all");
  const [sortBy, setSortBy] = useState("total_outstanding");
  const [sortDir, setSortDir] = useState("desc");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [savedViews, setSavedViews] = useState(() => JSON.parse(localStorage.getItem("rf_views") || "[]"));

  useEffect(() => { api.get("/portfolio/filters").then((r) => setFilters(r.data)).catch(() => {}); }, [version]);

  const params = { search, product, geography, bucket, industry, borrower };

  const fetchLoans = useCallback(() => {
    setLoading(true);
    api.get("/portfolio/loans", { params: { ...params, sort_by: sortBy, sort_dir: sortDir, page, page_size: 20 } })
      .then((r) => setData(r.data)).finally(() => setLoading(false));
  }, [search, product, geography, bucket, industry, borrower, sortBy, sortDir, page, version]);

  useEffect(() => { const t = setTimeout(fetchLoans, 300); return () => clearTimeout(t); }, [fetchLoans]);
  useEffect(() => { setPage(1); }, [search, product, geography, bucket, industry, borrower]);

  const exportCsv = async () => {
    const res = await api.get("/portfolio/export", { params, responseType: "blob" });
    const url = window.URL.createObjectURL(new Blob([res.data]));
    const a = document.createElement("a");
    a.href = url; a.download = "loan_book.csv"; a.click();
    window.URL.revokeObjectURL(url);
  };

  const saveView = () => {
    const name = window.prompt("Name this quick view:");
    if (!name) return;
    const next = [...savedViews.filter((v) => v.name !== name), { name, product, geography, bucket }];
    setSavedViews(next);
    localStorage.setItem("rf_views", JSON.stringify(next));
  };

  const applyView = (v) => { setProduct(v.product); setGeography(v.geography); setBucket(v.bucket); };
  const removeView = (name) => {
    const next = savedViews.filter((v) => v.name !== name);
    setSavedViews(next);
    localStorage.setItem("rf_views", JSON.stringify(next));
  };

  const toggleSort = (col) => {
    if (sortBy === col) setSortDir(sortDir === "desc" ? "asc" : "desc");
    else { setSortBy(col); setSortDir("desc"); }
  };

  const totalPages = Math.max(1, Math.ceil(data.total / 20));

  const columns = [
    { key: "facility", label: "Facility", sortable: false },
    { key: "borrower", label: "Borrower", sortable: false },
    { key: "product", label: "Product", sortable: false },
    { key: "geography", label: "State", sortable: false },
    { key: "sanction_amount", label: "Sanctioned", sortable: true, align: "right" },
    { key: "total_outstanding", label: "Outstanding", sortable: true, align: "right" },
    { key: "roi", label: "ROI", sortable: true, align: "right" },
    { key: "ltv", label: "LTV", sortable: true, align: "right" },
    { key: "principal_overdue", label: "Overdue", sortable: true, align: "right" },
    { key: "bucket", label: "DPD", sortable: false },
  ];

  return (
    <div className="p-6 lg:p-8" data-testid="portfolio-page">
      <header className="mb-6">
        <h1 className="font-display text-3xl font-extrabold text-[#1a1a1b]">Loan Book</h1>
        <p className="text-slate-500 mt-1">{formatNumber(data.total)} facilities · search, filter & drill into any loan</p>
      </header>
      <HistoricalBookBasis prefix="portfolio" />

      <div className="rf-card p-4 mb-5">
        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[220px]">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="portfolio-search"
              placeholder="Search borrower or facility no…"
              className="w-full pl-10 pr-4 py-2.5 rounded-sm border border-slate-300 text-sm focus:border-[#b3122a] focus:ring-2 focus:ring-[#b3122a]/20 outline-none transition"
            />
          </div>
          <Filter size={16} className="text-slate-400" />
          <Select value={product} onChange={setProduct} options={filters.products} label="All products" testid="filter-product" />
          <Select value={geography} onChange={setGeography} options={filters.geographies} label="All states" testid="filter-geography" />
          <Select value={bucket} onChange={setBucket} options={filters.buckets} label="All DPD" testid="filter-bucket" />
          <button onClick={saveView} data-testid="save-view-btn" className="flex items-center gap-1.5 px-3 py-2.5 rounded-sm border border-slate-300 text-sm font-semibold text-slate-600 hover:bg-slate-50 transition"><Star size={15} /> Save view</button>
          <button onClick={exportCsv} data-testid="export-csv-btn" className="flex items-center gap-1.5 px-3 py-2.5 rounded-sm bg-[#b3122a] text-white text-sm font-semibold hover:bg-[#8c0d20] transition"><Download size={15} /> Export CSV</button>
        </div>
        {(product !== "all" || geography !== "all" || bucket !== "all" || industry !== "all" || borrower !== "all" || search) && (
          <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-slate-100" data-testid="active-filters">
            <span className="text-xs font-semibold text-slate-400 self-center">Active filters:</span>
            {[
              borrower !== "all" && { k: "borrower", label: `Borrower: ${borrower}`, clear: () => setBorrower("all") },
              search && { k: "search", label: `Search: ${search}`, clear: () => setSearch("") },
              product !== "all" && { k: "product", label: product, clear: () => setProduct("all") },
              geography !== "all" && { k: "geography", label: geography, clear: () => setGeography("all") },
              bucket !== "all" && { k: "bucket", label: bucket, clear: () => setBucket("all") },
              industry !== "all" && { k: "industry", label: `Industry: ${industry}`, clear: () => setIndustry("all") },
            ].filter(Boolean).map((f) => (
              <span key={f.k} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#b3122a]/10 border border-[#b3122a]/20 text-xs font-semibold text-[#b3122a]" data-testid={`active-filter-${f.k}`}>
                {f.label}
                <button onClick={f.clear} className="hover:text-[#8c0d20]">×</button>
              </span>
            ))}
            <button onClick={() => { setSearch(""); setProduct("all"); setGeography("all"); setBucket("all"); setIndustry("all"); setBorrower("all"); }}
              className="text-xs font-semibold text-slate-400 hover:text-[#b3122a] self-center" data-testid="clear-all-filters">Clear all</button>
          </div>
        )}
        {savedViews.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-slate-100" data-testid="quick-views">
            <span className="text-xs font-semibold text-slate-400 self-center">Quick views:</span>
            {savedViews.map((v) => (
              <span key={v.name} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-slate-100 border border-slate-200 text-xs font-semibold text-slate-700">
                <button onClick={() => applyView(v)} className="hover:text-[#b3122a]">{v.name}</button>
                <button onClick={() => removeView(v.name)} className="text-slate-400 hover:text-[#b3122a]">×</button>
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="rf-card overflow-hidden">
        <div className="overflow-x-auto">
          <LedgerTable labels={columns.map(c => c.label)} className="w-full text-sm" data-testid="portfolio-table">
            <thead>
              <tr className="bg-[#111111] text-slate-300">
                {columns.map((col) => (
                  <th
                    key={col.key}
                    onClick={() => col.sortable && toggleSort(col.key)}
                    className={`px-4 py-3 font-semibold text-xs uppercase tracking-wide whitespace-nowrap ${col.align === "right" ? "text-right" : "text-left"} ${col.sortable ? "cursor-pointer select-none hover:text-white" : ""}`}
                  >
                    <span className={`inline-flex items-center gap-1 ${col.align === "right" ? "flex-row-reverse" : ""}`}>
                      {col.label}
                      {col.sortable && <ArrowUpDown size={12} className={sortBy === col.key ? "text-[#b3122a]" : "opacity-40"} />}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={columns.length} className="px-4 py-10 text-center text-slate-400">Loading…</td></tr>}
              {!loading && data.items.length === 0 && <tr><td colSpan={columns.length} className="px-4 py-10 text-center text-slate-400" data-testid="portfolio-empty">No facilities match your filters.</td></tr>}
              {!loading && data.items.map((l) => (
                <tr key={l.id} onClick={() => navigate(`/portfolio/${l.id}`)} className="rf-table-row border-t border-slate-100" data-testid={`loan-row-${l.facility}`}>
                  <td className="px-4 py-3 font-mono text-xs text-slate-500 whitespace-nowrap">{l.facility}</td>
                  <td className="px-4 py-3 font-semibold text-[#1a1a1b] whitespace-nowrap">{l.borrower}</td>
                  <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{l.product}</td>
                  <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{l.geography}</td>
                  <td className="px-4 py-3 text-right text-slate-600 whitespace-nowrap" data-testid={`portfolio-sanction-${l.id}`}>{formatINRFull(l.sanction_amount)}</td>
                  <td className="px-4 py-3 text-right font-semibold text-[#1a1a1b] whitespace-nowrap" data-testid={`portfolio-outstanding-${l.id}`}>{formatINRFull(l.total_outstanding)}</td>
                  <td className="px-4 py-3 text-right text-slate-600">{l.roi}%</td>
                  <td className="px-4 py-3 text-right text-slate-600">{(l.ltv * 100).toFixed(0)}%</td>
                  <td className={`px-4 py-3 text-right font-semibold whitespace-nowrap ${l.principal_overdue > 0 ? "text-[#b3122a]" : "text-slate-400"}`} data-testid={`portfolio-overdue-${l.id}`}>{formatINRFull(l.principal_overdue)}</td>
                  <td className="px-4 py-3"><Bucket b={l.bucket} /></td>
                </tr>
              ))}
            </tbody>
          </LedgerTable>
        </div>

        <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100">
          <span className="text-sm text-slate-500">Page {data.page} of {totalPages} · {formatNumber(data.total)} facilities</span>
          <div className="flex gap-2">
            <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1} data-testid="page-prev" className="px-3 py-1.5 rounded-sm border border-slate-300 text-sm font-medium text-slate-600 disabled:opacity-40 hover:bg-slate-50 transition flex items-center gap-1"><ChevronLeft size={16} /> Prev</button>
            <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages} data-testid="page-next" className="px-3 py-1.5 rounded-sm border border-slate-300 text-sm font-medium text-slate-600 disabled:opacity-40 hover:bg-slate-50 transition flex items-center gap-1">Next <ChevronRight size={16} /></button>
          </div>
        </div>
      </div>
    </div>
  );
}
